import { get, post } from "./axios";
import { SupportedLocales } from "../views/IntlContext";
import { Tab } from "../pages/meter/statistics/components";

const prefix = "/medcursor/api";

export const INTEGRATED_SERVICE = {
  alexa: "alexa",
  googleAssistant: "googleAssistant",
  googleFit: "googleFit",
  appleHealth: "appleHealth",
  healthConnect: "healthConnect",
} as const;

export type ServicePlatformType =
  (typeof INTEGRATED_SERVICE)[keyof typeof INTEGRATED_SERVICE];

export const SERVICE_TYPE = {
  voice: "voice",
  health:"health"
} as const

export type ServiceType =
  (typeof SERVICE_TYPE)[keyof typeof SERVICE_TYPE];

interface PlatformConfig {
  supportedPlatforms: string[];
  serviceLabel: ThirdPartyService;
  enabled: boolean;
  alias: string;
}

export enum ThirdPartyService {
  alexa = "Alexa",
  googleAssistant = "Google Assistant",
  googleFit = "Google Fit",
  appleHealth = "Apple Health",
  healthConnect = "Health Connect",
}

export const PLATFORM_CONFIGS: Record<ServicePlatformType, PlatformConfig> = {
  alexa: {
    supportedPlatforms: ["android", "ios"],
    serviceLabel: ThirdPartyService.alexa,
    enabled: false,
    alias: "alexa",
  },
  googleAssistant: {
    supportedPlatforms: ["android", "ios"],
    serviceLabel: ThirdPartyService.googleAssistant,
    enabled: true,
    alias: "google",
  },
  googleFit: {
    supportedPlatforms: ["android"],
    serviceLabel: ThirdPartyService.googleFit,
    enabled: true,
    alias: "googlefit",
  },
  healthConnect: {
    supportedPlatforms: ["android"],
    serviceLabel: ThirdPartyService.healthConnect,
    enabled: false,
    alias: "healthConnect",
  },
  appleHealth: {
    supportedPlatforms: ["ios"],
    serviceLabel: ThirdPartyService.appleHealth,
    enabled: true,
    alias: "applehealth",
  },
};

export interface IDeviceInfo {
  nickname: string;
  name: string;
  productModel: string;
  mcuVersion: string;
}

export type IAuth = {
  token?: string;
  lang?: SupportedLocales;
};

export type IDeviceBody = IAuth & {
  id: string;
};

export type ICommandBody = IAuth & {
  productId?: string | number;
};

export type IProductManualBody = IAuth & {
  productId?: string | number;
};

export type IResetPasswordBody = IAuth & {
  data: {
    newPassword: string;
    code: string;
  };
};

export type IAutLoginBody = {
  params: {
    email: string;
    password: string;
    client_id: string;
    redirect_uri: string;
    state: string;
  };
};

export type IProduct = {
  id: string;
  name: string;
  picture: string;
  nickname: string;
  model: string;
  status: null;
  type: number;
  productId: string;
  productName: string;
  deviceId: string;
  temperatureType: number;
  productTypeId: string | null;
  productType: string | null;
  paring: number;
  reminderType: null;
};

export type IThirdPartyService = {
  id: number;
  type: string | null;
  showFlag: ServicePlatformType;
  soundType?: string | null;
  status?: string | null;
  lang?: string | null;
  image: string;
  redirect?: string | null;
  name: string;
  createTime?: string | null;
  updateTime?: string | null;
};

export type IMeterDetailBody = IAuth & {
  params: {
    id: number;
    deviceId: string;
  };
};

export type IUpdateRemarkBody = IAuth & {
  data: {
    id: number;
    remark: string;
  };
};

export type IServicesProductBody = IAuth & {
  data: {
    id?: number;
  };
};

export type IMeterHistoryBody = IAuth & {
  params: {
    deviceId: string;
    userId: string;
    size: number;
    current: number;
    startDate?: number;
    endDate?: number;
    type?: Tab;
  };
};

export type IMeterUserBody = IAuth & {
  params: {
    deviceId: string;
  };
};

export type IMeterQueryParams = {
  appUserId: string;
  deviceId: string;
  userId: string;
  dateType: number;
  startTime: number;
  endTime: number;
  lang: SupportedLocales;
};

export type IMeterShareBody = IAuth & {
  params: IMeterQueryParams;
};

export type IMeterHistoryDeleteBody = IAuth & {
  params: {
    id: number;
  };
};

export const deviceInfo = (data: IDeviceBody) => {
  return get({
    ...data,
    url: `${prefix}/device/info/${data.id}`,
  });
};

export const thirdPartyVoiceServices = (data: IAuth) => {
  return get({ ...data, url: `${prefix}/common/thirdSound/listAll` });
};

export const supportGoogleProduct = (data: IServicesProductBody) => {
  return get({
    ...data,
    url: `${prefix}/product/google?soundId=${data.data.id}`,
  });
};

export const supportAlexaProduct = (data: IServicesProductBody) => {
  return get({
    ...data,
    url: `${prefix}/product/alexa?soundId=${data.data.id}`,
  });
};

// third health apple health and google fit  details
export const supportHealthProduct = (data: IServicesProductBody) => {
  return get({
    ...data,
    url: `${prefix}/product/health?soundId=${data.data.id}`,
  });
};

export const userDeviceList = (data: IAuth) => {
  return get({ ...data, url: `${prefix}/user/device-h5` });
};

export const commandList = (data: ICommandBody) => {
  return get({
    ...data,
    url: `${prefix}/product/command/list?productId=${data.productId}`,
  });
};

export const productManual = (data: IProductManualBody) => {
  return get({
    ...data,
    url: `${prefix}/product/explain?productId=${data.productId}`,
  });
};

export const resetPassword = (data: IResetPasswordBody) => {
  return post({
    ...data,
    url: `${prefix}/user/forget`,
  });
};

export const alexaAuth = (data: IAutLoginBody) => {
  return get({
    ...data,
    baseURL: process.env.REACT_APP_ALEXA_API_URL,
    url: `/user/login`,
  });
};

export const googleAuth = (data: IAutLoginBody) => {
  return get({
    ...data,
    baseURL: process.env.REACT_APP_GOOGLE_API_URL,
    url: `/user/login`,
  });
};

export const alexaUnlink = (data: IAuth) => {
  return post({
    ...data,
    url: `${prefix}/alexa/disable`,
  });
};

export const meterDetails = (data: IMeterDetailBody) => {
  return get({
    ...data,
    params: data.params,
    url: `${prefix}/bm/device/share/detail`,
  });
};

export const updateRemark = (data: IUpdateRemarkBody) => {
  return post({
    ...data,
    // params: data.data,
    url: `${prefix}/bm/device/share/remark/update`,
  });
};

export const meterHistory = (data: IMeterHistoryBody) => {
  return get({
    ...data,
    params: data.params,
    url: `${prefix}/bm/device/history`,
  });
};

export const meterUser = (data: IMeterUserBody) => {
  return get({
    ...data,
    params: data.params,
    url: `${prefix}/bm/device/user/list`,
  });
};

export const meterShareList = (data: IMeterShareBody) => {
  return get({
    ...data,
    params: data.params,
    url: `${prefix}/bm/device/share/list`,
  });
};

export const meterShareChart = (data: IMeterShareBody) => {
  return get({
    ...data,
    params: data.params,
    url: `${prefix}/bm/device/share/chart`,
  });
};

export const meterStatisticsChart = (data: IMeterHistoryBody) => {
  return get({
    ...data,
    params: data.params,
    url: `${prefix}/bm/device/statistics`,
  });
};

export const meterHistoryDelete = (data: IMeterHistoryDeleteBody) => {
  return post({
    ...data,
    url: `${prefix}/bm/device/history/del/${data.params.id}`,
  });
};
